/**
 * Client-side campaign hydration. Reads the publicly-readable active rows in
 * `public.promo_campaigns` and publishes them into the shared pricing
 * registry so the cart drawer and the product page agree with the server.
 *
 * Display only — the payable amount is always recomputed server-side.
 */
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  campaignFromRow,
  findLiveCampaign,
  isCampaignLive,
  setActiveCampaigns,
  type PromoCampaign,
  type PromoCampaignRow,
} from "./inaugural";

const COLUMNS =
  "id,name,product_handle,campaign_type,qualifying_qty,offer_price_paise,prepaid_discount_bps,max_per_order,starts_at,expires_at,timezone,active";

async function fetchCampaigns(): Promise<PromoCampaign[]> {
  const { data, error } = await supabase
    .from("promo_campaigns")
    .select(COLUMNS)
    .eq("active", true);
  if (error || !data) {
    // Never keep stale promotional pricing alive on a failed fetch.
    setActiveCampaigns([]);
    return [];
  }
  const rows = (data as unknown as PromoCampaignRow[]).map(campaignFromRow);
  setActiveCampaigns(rows);
  return rows;
}

export function usePromoCampaigns() {
  return useQuery({
    queryKey: ["promo-campaigns"],
    queryFn: fetchCampaigns,
    staleTime: 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
    retry: 1,
  });
}

/**
 * Ticks every 15s so a page left open across the expiry moment drops the
 * offer on its own, without a refresh.
 */
function useNow(intervalMs = 15_000): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now;
}

/** The live inaugural campaign for one product, or null. */
export function useInauguralCampaign(handle: string | undefined): PromoCampaign | null {
  const { data } = usePromoCampaigns();
  const now = useNow();
  return findLiveCampaign(data ?? [], handle, now);
}

/** Every campaign that is live right now (any product). */
export function useLiveCampaigns(): PromoCampaign[] {
  const { data } = usePromoCampaigns();
  const now = useNow();
  return (data ?? []).filter((c) => isCampaignLive(c, now));
}
